import { useEffect, useState } from 'react';
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { ContentCopyOutlined, ExpandMore } from '@mui/icons-material';

import {
  FHAccordion,
  FHButton,
  FHConfirmDialog,
  FHInputField,
  FHRadio,
  FHRadioGroup,
  FHSmartTextarea,
  FHTooltip,
  FSwitch,
} from '../../../../base';
import TargetProgress from '../common/components/targetProgress';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import { capitalizeFirstLetter, validateSchema } from '../../../../../helpers';
import {
  FORTA_BLUE,
  FORTA_WHITE,
  FORTA_ERROR,
  FORTA_GRAY4,
  FORTA_GRAY2,
  FORTA_YELLOW,
} from '../../../../../utils/colors';
import { getTargetTrialSummary } from '../common/utils/general';
import { getV1TargetDuplicationData } from './utils/general';
import { targetSchema } from '../common/utils/validationSchema';
import {
  MAXIMUM_TARGET_INSTRUCTION_LENGTH,
  MINIMUM_REQUIRED_SENTENCE_LENGTH,
  Themes,
} from '../../../../../constants';
import {
  GoalTypes,
  GOAL_TARGET_NAME_MIN_CHARS,
  GOAL_TARGET_NAME_MAX_CHARS,
  REASON_FOR_DEACTIVATE_MAX_CHARS,
  EventDurationMeasurementUnit,
  iconButtonStyle,
  TreatmentPlanResourceVersion,
} from '../common/constants/general';
import TrashIcon from '../../../../svgs/TrashIcon';
import EditOutlinedIcon from '../../../../svgs/EditOutlinedIcon';
import { targetDescriptionBlacklist } from '../../../../../pages/constants';
import UnsavedIndicator from '../../components/unsavedIndicator';

import type { RootState } from '../../../../../store';
import type { ITreatmentPlanGoalTarget } from '../../../../../utils/types';
import type { ChangeEvent } from 'react';

interface GoalTargetItemProps {
  index: number;
  goalId: number;
  target: ITreatmentPlanGoalTarget;
  saving: boolean;
  goalType: GoalTypes;
  goalTargets: ITreatmentPlanGoalTarget[];
  goalActive: boolean;
  onSave: (target: ITreatmentPlanGoalTarget) => void;
  onDelete: () => void;
  onAddTargetWithValues: (data: any) => void;
  onUpdateField: (key: string, value: any) => void;
  onUpdateTargetActivation: (reason: string) => void;
  onCancelTargetChange: (target: ITreatmentPlanGoalTarget | null) => void;
}

function GoalTargetItem(props: GoalTargetItemProps) {
  const {
    index,
    goalId,
    target,
    saving,
    goalType,
    goalTargets,
    goalActive,
    onSave,
    onDelete,
    onUpdateField,
    onAddTargetWithValues,
    onUpdateTargetActivation,
    onCancelTargetChange,
  } = props;
  const { tpGoalsTrialSummary } = useSelector((state: RootState) => state.goals);
  const isTpEditable = useTreatmentPlanEditable();
  const [isEditing, setIsEditing] = useState<boolean>(true);
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [activeStatus, setActiveStatus] = useState<boolean>(true);
  const [label, setLabel] = useState<string>('');
  const [instruction, setInstruction] = useState<string>('');
  const [isEventDurationRequired, setIsEventDurationRequired] = useState<boolean>(false);
  const [eventDurationMeasurementUnit, setEventDurationMeasurementUnit] =
    useState<EventDurationMeasurementUnit | null>(null);
  const [reason, setReason] = useState<string>('');
  const [isTargetValid, setIsTargetValid] = useState<boolean>(false);
  const [errors, setErrors] = useState<any>(null);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [prevTarget, setPrevTarget] = useState<ITreatmentPlanGoalTarget | null>(null);
  const targetTrialSummary = getTargetTrialSummary(tpGoalsTrialSummary, goalId, target?.id ?? 0);

  useEffect(() => {
    if (!target) return;

    setLabel(target.label ?? '');
    setActiveStatus(target.is_active ?? true);
    setInstruction(target.instruction ?? '');
    setIsEventDurationRequired(target.is_event_duration_required ?? false);
    setEventDurationMeasurementUnit(target.event_duration_measurement_unit ?? null);
    setIsEditing(target.is_editing ?? false);

    // Create complete data object for validation
    const data = {
      ...target,
      target_version: TreatmentPlanResourceVersion.V1,
    };

    // Validate only the changed field
    const newErrors: { [key: string]: boolean } = {};
    Object.keys(data).forEach((key) => {
      const isValidField = validateSchema(targetSchema, key, data);
      newErrors[key] = !isValidField;
    });
    setErrors(newErrors);
  }, [target]);

  useEffect(() => {
    setIsEditable(isTpEditable && goalActive);
  }, [isTpEditable, goalActive]);

  useEffect(() => {
    if (!errors) {
      setIsTargetValid(false);
      return;
    }

    const isValid = !Object.keys(errors).some((key) => errors[key]);
    setIsTargetValid(isValid);
  }, [errors]);

  const handleSwitchToggle = (checked: boolean) => {
    const activeTargets = goalTargets.filter((target) => target.is_active);
    if (!checked && goalActive && activeTargets.length === 1) {
      toast.error('Goal should have another active target to deactivate this target.');
      return;
    }

    setActiveStatus(checked);
    setReason('');
  };

  const handleUpdateField = (key: string, value: string, isBlurred: boolean) => {
    if (isBlurred) {
      onUpdateField(key, value);
    } else {
      if (key === 'label') setLabel(value);
      if (key === 'instruction') setInstruction(value);
    }

    const data = {
      label: key === 'label' ? value : target?.label,
      instruction: key === 'instruction' ? value : target?.instruction,
      target_version: TreatmentPlanResourceVersion.V1,
    };

    const newErrors = { ...errors };
    const isValidField = validateSchema(targetSchema, key, data);
    newErrors[key] = !isValidField;
    setErrors(newErrors);
  };

  const handleCancel = () => {
    setShowConfirmation(false);
    setIsEditing(false);
    onCancelTargetChange(prevTarget);
  };

  const validateTarget = (showAlerts = false) => {
    if (label?.length < GOAL_TARGET_NAME_MIN_CHARS) {
      if (showAlerts) {
        toast.error(`Target name requires at least ${GOAL_TARGET_NAME_MIN_CHARS} characters.`);
      }
      return false;
    }

    if (label?.length > GOAL_TARGET_NAME_MAX_CHARS) {
      if (showAlerts) {
        toast.error(`Target name requires maximum ${GOAL_TARGET_NAME_MAX_CHARS} characters.`);
      }
      return false;
    }

    if (instruction?.length < MINIMUM_REQUIRED_SENTENCE_LENGTH) {
      if (showAlerts) {
        toast.error(
          `Target instruction requires at least ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters.`,
        );
      }
      return false;
    }

    if (instruction?.length > MAXIMUM_TARGET_INSTRUCTION_LENGTH) {
      if (showAlerts) {
        toast.error(
          `Target instruction has more than ${MAXIMUM_TARGET_INSTRUCTION_LENGTH} characters.`,
        );
      }
      return false;
    }

    return true;
  };

  const handleSaveTarget = () => {
    const isValid = validateTarget(true);
    if (!isValid) return;

    let targetData = {
      ...target,
      instruction,
      label,
      reason,
      is_editing: false,
    };
    if (goalType === GoalTypes.BehaviorReduction) {
      targetData = {
        ...targetData,
        is_event_duration_required: isEventDurationRequired,
        event_duration_measurement_unit: eventDurationMeasurementUnit,
      };
    }
    onSave(targetData);
  };

  const handleEditingClick = () => {
    onUpdateField('is_editing', !isEditing);
    if (!isEditing) {
      setPrevTarget(target);
    }
  };

  const isValid = validateTarget();

  const hasUnsavedChanges = target?.dirty && target?.is_editing;

  return (
    <Box
      sx={{
        px: 2,
        borderBottom: `2px solid ${FORTA_BLUE.light3}`,
        '&:last-of-type': {
          borderBottom: 'none',
        },
      }}
      data-testid={`targetContainer_${target.label}`}
    >
      <FHAccordion
        colorTheme={Themes.Primary}
        defaultExpanded={false}
        expanded={process.env.VITE_TEST === 'true' ? true : Boolean(target?.expanded)}
        hasErrors={!isValid}
        hasWarnings={hasUnsavedChanges}
      >
        <Box sx={{ position: 'relative' }}>
          {hasUnsavedChanges && <UnsavedIndicator right={'-40px'} />}
          <AccordionSummary
            expandIcon={<ExpandMore data-testid={`expand_target_${index}`} />}
            sx={{
              padding: '16px',
              '&.Mui-focusVisible': {
                backgroundColor: 'transparent',
              },
              '& .MuiAccordionSummary-expandIconWrapper': {
                position: 'absolute',
                right: 10,
                top: 24,
              },
            }}
            onClick={() => onUpdateField('expanded', !target?.expanded)}
          >
            <Grid container>
              <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  textAlign={'left'}
                  variant="h4"
                  fontWeight={'bold'}
                  mb={0.5}
                  color={FORTA_BLUE.primary}
                >
                  {`${index + 1}: ${capitalizeFirstLetter(label ?? '')}`}
                </Typography>
                {isEditing && (
                  <Typography
                    ml={2}
                    textAlign={'left'}
                    alignSelf="center"
                    fontWeight={'700'}
                    lineHeight={'21px'}
                    sx={{
                      color: isTargetValid ? FORTA_BLUE.light1 : FORTA_ERROR,
                    }}
                  >
                    Attention: All fields with * are required to be filled
                  </Typography>
                )}
              </Grid>
              <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                {!isEditing && (
                  <>
                    <FHTooltip
                      tooltipBorderRadius="8px"
                      tooltipBoxShadow="0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
                      title={<Typography>Delete target</Typography>}
                    >
                      <IconButton
                        data-testid={`deleteTarget_${target?.label}`}
                        disabled={saving}
                        hidden={!isEditable}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete();
                        }}
                        sx={iconButtonStyle}
                      >
                        <TrashIcon color={FORTA_BLUE.primary} />
                      </IconButton>
                    </FHTooltip>
                    <FHTooltip
                      tooltipBorderRadius="8px"
                      tooltipBoxShadow="0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
                      title={<Typography>Edit target</Typography>}
                    >
                      <IconButton
                        data-testid={`editTarget_${target?.label}`}
                        sx={iconButtonStyle}
                        hidden={!isEditable}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditingClick();
                        }}
                      >
                        <EditOutlinedIcon color={FORTA_BLUE.primary} />
                      </IconButton>
                    </FHTooltip>
                    {isEditable && target?.id && (
                      <FHTooltip
                        tooltipBorderRadius="8px"
                        tooltipBoxShadow="0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
                        title={<Typography>Duplicate target</Typography>}
                      >
                        <IconButton
                          data-testid={`editTarget_${target?.label}`}
                          sx={iconButtonStyle}
                          hidden={!isTpEditable}
                          disabled={saving || !isTpEditable}
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddTargetWithValues(getV1TargetDuplicationData(target));
                          }}
                        >
                          <ContentCopyOutlined sx={{ color: FORTA_BLUE.primary }} />
                        </IconButton>
                      </FHTooltip>
                    )}
                  </>
                )}
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <FSwitch
                    data-testid={`goalTargetActiveStatus_${target?.label}_${activeStatus}`}
                    colorTheme={Themes.Primary}
                    coloredBackground
                    sx={{ mr: 4 }}
                    checked={activeStatus}
                    disabled={
                      saving || !isEditable || !(target != null && Object.hasOwn(target, 'id'))
                    }
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      e.stopPropagation();
                      handleSwitchToggle(e.target.checked);
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </AccordionSummary>
        </Box>
        {target?.expanded ? (
          <AccordionDetails sx={{ p: 3 }}>
            <Grid container>
              {isEditing && (
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <FHButton
                    data-testid="targetsCancelButtonTop"
                    sx={{
                      backgroundColor: FORTA_WHITE,
                      border: `1px solid ${FORTA_BLUE.light2}`,
                      color: FORTA_GRAY4,
                      mr: 3,
                      '&:hover': {
                        backgroundColor: FORTA_WHITE,
                        border: `1px solid ${FORTA_BLUE.light2}`,
                        color: FORTA_GRAY4,
                      },
                      '&.Mui-disabled': {
                        backgroundColor: FORTA_WHITE,
                        color: FORTA_GRAY2,
                      },
                    }}
                    disabled={!isEditable}
                    onClick={() => {
                      setShowConfirmation(true);
                    }}
                  >
                    Cancel
                  </FHButton>
                  <Box sx={{ position: 'relative' }}>
                    {target?.dirty && <UnsavedIndicator right={'-40px'} />}
                    <FHButton
                      data-testid={`targetSaveButtonTop`}
                      colorTheme={Themes.Primary}
                      onClick={handleSaveTarget}
                    >
                      SAVE TARGET
                    </FHButton>
                  </Box>
                </Grid>
              )}
              {isEditing && (
                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
                  <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
                    Target name
                  </Typography>
                  <FHInputField
                    data-testid="targetName"
                    inputHeight={40}
                    colorTheme={Themes.Primary}
                    coloredBackground
                    disabled={saving || !isEditable}
                    value={capitalizeFirstLetter(label ?? '')}
                    placeholder="Enter target name"
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleUpdateField('label', e.target.value, false)
                    }
                    onBlur={() => handleUpdateField('label', label, true)}
                    error={errors && errors['label']}
                    helperText={
                      errors && errors['label']
                        ? `${label?.length}/${GOAL_TARGET_NAME_MAX_CHARS}`
                        : ''
                    }
                  />
                </Grid>
              )}
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', flexDirection: 'column', mt: isEditing ? 2 : 0 }}
              >
                <Box display={'flex'}>
                  <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
                    Target instructions
                  </Typography>
                  {isEditing && (
                    <Typography textAlign={'left'} ml={0.5}>
                      (include prompting, environment, and materials)
                    </Typography>
                  )}
                </Box>
                {isEditing ? (
                  <FHSmartTextarea
                    id={`goal_target_instructions-${target.id}`}
                    data-testid={`targetInstructions`}
                    sx={{ flex: 1 }}
                    fullWidth
                    colorTheme={Themes.Primary}
                    coloredBackground
                    hightlightOnDetection
                    heightlightColour={FORTA_YELLOW}
                    blackListedWordsPhrases={targetDescriptionBlacklist}
                    disabled={saving || !isEditable}
                    placeholder="Describe the target instructions"
                    value={instruction}
                    onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
                      handleUpdateField('instruction', e.target.value, false)
                    }
                    onBlur={() => handleUpdateField('instruction', instruction, true)}
                    onCloseExpandDialog={() => {
                      handleUpdateField('instruction', instruction, true);
                    }}
                    error={errors && errors['instruction']}
                    helperText={
                      errors && errors['instruction']
                        ? instruction.trim().length > MAXIMUM_TARGET_INSTRUCTION_LENGTH
                          ? `Maximum ${MAXIMUM_TARGET_INSTRUCTION_LENGTH} characters`
                          : `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                        : ''
                    }
                  />
                ) : (
                  <Typography textAlign={'left'}>{instruction}</Typography>
                )}
              </Grid>
              {goalType === GoalTypes.BehaviorReduction && isEventDurationRequired && (
                <Grid item xs={4} mt={2}>
                  <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
                    Capture the duration of the event?
                  </Typography>
                  {isEditing ? (
                    <FHRadioGroup
                      data-testid="isEventDurationRequiredRadioGroup"
                      rowGap="16px"
                      name="capture-the-duration-of-the-event"
                      colorTheme={Themes.Primary}
                      coloredBackground
                      value={isEventDurationRequired ? 'Yes' : 'No'}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        // To keep the text field data on blur
                        setTimeout(() => {
                          onUpdateField(
                            'is_event_duration_required',
                            e.target.value === 'Yes' ? true : false,
                          );
                        });
                      }}
                    >
                      <FormControlLabel
                        data-testid="isEventDurationRequiredRadioYesChoice"
                        sx={{ width: 'fit-content' }}
                        value="Yes"
                        control={<FHRadio colorTheme={Themes.Primary} />}
                        label="Yes"
                        disabled={true}
                      />
                      <FormControlLabel
                        data-testid="isEventDurationRequiredRadioNoChoice"
                        sx={{ width: 'fit-content' }}
                        value="No"
                        control={<FHRadio colorTheme={Themes.Primary} />}
                        label="No"
                        disabled={true}
                      />
                    </FHRadioGroup>
                  ) : (
                    <Typography textAlign={'left'}>
                      {isEventDurationRequired ? 'Yes' : 'No'}
                    </Typography>
                  )}
                </Grid>
              )}
              {goalType === GoalTypes.BehaviorReduction && isEventDurationRequired && (
                <Grid item xs={8} mt={2}>
                  <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
                    Unit of duration for target
                  </Typography>
                  {isEditing ? (
                    <FHRadioGroup
                      data-testid="targetUnitsRadioGroup"
                      rowGap="16px"
                      colorTheme={Themes.Primary}
                      coloredBackground
                      name="unit-of-duration-for-target"
                      value={eventDurationMeasurementUnit}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        onUpdateField(
                          'event_duration_measurement_unit',
                          Object.values(EventDurationMeasurementUnit).find(
                            (unit) => unit === e.target.value,
                          ) ?? null,
                        )
                      }
                    >
                      <FormControlLabel
                        data-testid="targetUnitSeconds"
                        sx={{ width: 'fit-content' }}
                        disabled={true}
                        value={EventDurationMeasurementUnit.Second}
                        control={<FHRadio colorTheme={Themes.Primary} />}
                        label="Seconds"
                      />
                      <FormControlLabel
                        data-testid="targetUnitMinuts"
                        sx={{ width: 'fit-content' }}
                        disabled={true}
                        value={EventDurationMeasurementUnit.Minute}
                        control={<FHRadio colorTheme={Themes.Primary} />}
                        label="Minutes"
                      />
                      <FormControlLabel
                        data-testid="targetUnitHours"
                        sx={{ width: 'fit-content' }}
                        disabled={true}
                        value={EventDurationMeasurementUnit.Hour}
                        control={<FHRadio colorTheme={Themes.Primary} />}
                        label="Hours"
                      />
                    </FHRadioGroup>
                  ) : (
                    <Typography textAlign={'left'}>
                      {eventDurationMeasurementUnit
                        ?.charAt(0)
                        .toUpperCase()
                        .concat(eventDurationMeasurementUnit?.slice(1).toLowerCase()) + 's'}
                    </Typography>
                  )}
                </Grid>
              )}
              {target?.id && (
                <TargetProgress
                  goalId={goalId}
                  target={target}
                  trialSummary={targetTrialSummary}
                  onUpdateField={onUpdateField}
                />
              )}
              {isEditing && (
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }} mt={2}>
                  <FHButton
                    data-testid="targetsCancelButton"
                    sx={{
                      backgroundColor: FORTA_WHITE,
                      border: `1px solid ${FORTA_BLUE.light2}`,
                      color: FORTA_GRAY4,
                      mr: 3,
                      '&:hover': {
                        backgroundColor: FORTA_WHITE,
                        border: `1px solid ${FORTA_BLUE.light2}`,
                        color: FORTA_GRAY4,
                      },
                      '&.Mui-disabled': {
                        backgroundColor: FORTA_WHITE,
                        color: FORTA_GRAY2,
                      },
                    }}
                    disabled={!isEditable}
                    onClick={() => {
                      setShowConfirmation(true);
                    }}
                  >
                    Cancel
                  </FHButton>
                  <Box sx={{ position: 'relative' }}>
                    {target?.dirty && <UnsavedIndicator right="-20px" top="-15px" />}
                    <FHButton
                      data-testid={`targetSaveButton`}
                      colorTheme={Themes.Primary}
                      onClick={handleSaveTarget}
                    >
                      SAVE TARGET
                    </FHButton>
                  </Box>
                </Grid>
              )}
              {target?.is_active !== activeStatus && (
                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
                  <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
                    Reason for status change
                  </Typography>
                  <FHInputField
                    data-testid={`targetStatusChangeReasonTextField_${target?.label}`}
                    sx={{ flex: 1 }}
                    inputHeight={40}
                    colorTheme={Themes.Primary}
                    coloredBackground
                    value={reason}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
                    onBlur={() => onUpdateTargetActivation(reason)}
                    error={errors && errors['reason']}
                    helperText={
                      errors && errors['reason']
                        ? `${REASON_FOR_DEACTIVATE_MAX_CHARS} character limit reached`
                        : ''
                    }
                  />
                </Grid>
              )}
              {!target?.is_editing && !target?.is_active && target?.is_active === activeStatus && (
                <Grid
                  data-testid={`targetDeactivationReason_${target?.label}_${target?.is_active}`}
                  item
                  xs={12}
                  sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}
                >
                  <Typography fontWeight={'bold'} textAlign={'left'} mb={1}>
                    Reason for deactivation
                  </Typography>
                  <Typography align="left">{target?.reason || ''}</Typography>
                </Grid>
              )}
              <FHConfirmDialog
                open={showConfirmation}
                colorTheme={Themes.Primary}
                title={'Cancel target'}
                content={'Are you sure you want to cancel? Any new information will be lost.'}
                submittext={'Cancel'}
                canceltext={'Return to target'}
                onClose={() => setShowConfirmation(false)}
                onSubmit={handleCancel}
              />
            </Grid>
          </AccordionDetails>
        ) : null}
      </FHAccordion>
    </Box>
  );
}

export default GoalTargetItem;
